import React from "react";
import { Compass, Columns, RefreshCw, Copy, ChevronUp, Sparkles, BookOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import SelfArt from "./SelfArt";
import type { AlternateSelf, AxisId } from "@/lib/infiniteSelves";
import { AXIS_ORDER, AXIS_LABELS } from "@/lib/infiniteSelves";
export default function SelfCard(props: {
  self: AlternateSelf;
  index: number;
  exploring: boolean;
  comparing: boolean;
  compareDisabled: boolean;
  onExplore: () => void;
  onToggleCompare: () => void;
  onRegenerate: () => void;
  onCopy: () => void;
  onBridge?: () => void;
}): JSX.Element {
  const {
    self,
    index,
    exploring,
    comparing,
    compareDisabled,
    onExplore,
    onToggleCompare,
    onRegenerate,
    onCopy,
    onBridge
  } = props;
  const {
    visual,
    scenarioLabel,
    name,
    title,
    timelineId,
    tagline,
    occupation,
    universe,
    axes,
    lifeGlance,
    life
  } = self;
  const cardHue = visual.hue;
  const isHighlighted = exploring || comparing;
  return <div className={`ql-card relative flex flex-col justify-between overflow-hidden rounded-xl border transition-all duration-300 ${isHighlighted ? "border-primary ring-2 ring-primary/50 shadow-[0_0_28px_var(--glow-primary)]" : "border-border hover:border-primary/50"}`} style={{
    boxShadow: isHighlighted ? `0 0 28px -4px hsl(${cardHue} 100% 50% / 0.35)` : undefined
  }}>
      {/* Top Accent Gradient Bar tinted with self's hue */}
      <div className="h-1.5 w-full shrink-0" style={{
      background: `linear-gradient(90deg, transparent, hsl(${cardHue} 100% 55%), hsl(${visual.hue2} 90% 50%), transparent)`
    }} />

      <div className="p-5 sm:p-6 space-y-5 flex-1 flex flex-col">
        {/* Scenario Label Eyebrow & Timeline Badge */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-primary truncate">
              {scenarioLabel}
            </span>
          </div>
          <Badge variant="outline" className="text-[10px] font-mono tracking-wider border-border/80 bg-muted/60 text-muted-foreground px-2 py-0.5 shrink-0">
            {timelineId}
          </Badge>
        </div>

        {/* Visualizer Art (Portrait on cosmic space) */}
        <div className="relative group/art">
          <SelfArt self={self} variant="card" />
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-lg opacity-0 group-hover/art:opacity-100 transition-opacity duration-300 ring-1 ring-primary/40 shadow-[inset_0_0_20px_var(--glow-primary)]" />
        </div>

        {/* Identity Headings */}
        <div className="space-y-1">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="font-serif text-xl sm:text-2xl font-bold tracking-tight text-foreground">
              {name}
            </h3>
            <span className="text-[11px] font-mono text-muted-foreground shrink-0">
              #{index + 1}
            </span>
          </div>
          <p className="text-xs sm:text-sm font-medium text-foreground/80 italic">
            {title}
          </p>
          <p className="text-xs text-primary font-mono pt-0.5">
            {occupation}
          </p>
        </div>

        {/* Tagline */}
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
          "{tagline}"
        </p>

        {/* Life at a Glance preview (Free) */}
        {lifeGlance && <div className="rounded-lg bg-card/75 border border-border/80 p-3 space-y-1.5 shadow-xs">
            <div className="flex items-center justify-between text-[10px] font-mono border-b border-border/50 pb-1">
              <span className="uppercase tracking-wider text-primary font-bold flex items-center gap-1">
                <Sparkles className="size-3 text-primary shrink-0" />
                <span>Life at a Glance</span>
              </span>
              <span className="text-foreground/90 font-semibold">
                Age {lifeGlance.age}
              </span>
            </div>
            <div className="space-y-0.5 text-[11px] font-sans text-muted-foreground">
              {lifeGlance.standing && <p className="truncate">
                  <span className="text-foreground/80 font-medium">Standing: </span>
                  {lifeGlance.standing}
                </p>}
              {lifeGlance.home && <p className="truncate">
                  <span className="text-foreground/80 font-medium">Home: </span>
                  {lifeGlance.home}
                </p>}
              {lifeGlance.society && <p className="truncate">
                  <span className="text-foreground/80 font-medium">Society: </span>
                  {lifeGlance.society}
                </p>}
            </div>

            {/* 13 Life chapters hint */}
            <div className="flex items-center justify-between pt-1 border-t border-border/40 text-[10px] font-mono">
              <span className="text-muted-foreground flex items-center gap-1">
                <BookOpen className="size-3 text-primary shrink-0" />
                <span>13 Life Chapters</span>
              </span>
              {life !== null ? <span className="text-primary font-semibold flex items-center gap-0.5">
                  <span>Full dossier included</span>
                </span> : null}
            </div>
          </div>}

        {/* Defining Dilemma Teaser (when present) */}
        {self.dossier?.dilemma && <div className="rounded-lg bg-card/60 border border-border/70 p-2.5 space-y-1">
            <span className="text-[10px] font-mono uppercase tracking-wider text-chart-3 font-semibold block flex items-center gap-1">
              <span className="inline-block size-1.5 rounded-full bg-chart-3 shrink-0" />
              Defining Dilemma
            </span>
            <p className="text-xs text-foreground/90 font-sans leading-relaxed line-clamp-2">
              {self.dossier.dilemma}
            </p>
          </div>}

        {/* Universe & Habitability info */}
        <div className="rounded-lg bg-muted/40 border border-border/60 p-3 flex items-center justify-between gap-2 text-xs">
          <div className="space-y-0.5 min-w-0">
            <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block truncate">
              Cosmic Environment
            </span>
            <span className="font-semibold text-foreground truncate block">
              {universe.name}
            </span>
          </div>
          <Badge variant="outline" className="text-[10px] font-mono shrink-0 border-primary/40 text-primary bg-primary/10 shadow-[0_0_8px_var(--glow-primary)]">
            {universe.profile?.habitabilityLabel ?? `${universe.condition}`}
          </Badge>
        </div>

        {/* 5 Axis Meters as glowing progress bars */}
        <div className="space-y-2 border-t border-border/60 pt-4 mt-auto">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground block">
              Dimensional Alignment
            </span>
            <span className="text-[9px] font-mono text-primary">VECTORS</span>
          </div>
          <div className="space-y-1.5">
            {AXIS_ORDER.map((axisKey: AxisId) => {
            const val = axes[axisKey] ?? 50;
            const label = AXIS_LABELS[axisKey] ?? axisKey;
            return <div key={axisKey} className="flex items-center gap-2 text-xs">
                  <span className="w-20 text-[11px] text-muted-foreground truncate font-sans">
                    {label}
                  </span>
                  <div className="flex-1 h-1.5 rounded-full bg-muted/80 overflow-hidden border border-border/40">
                    <div className="h-full rounded-full transition-all duration-300 shadow-[0_0_8px_var(--glow-primary)]" style={{
                  width: `${Math.max(4, Math.min(100, val))}%`,
                  backgroundColor: `hsl(${cardHue} 100% 50%)`
                }} />
                  </div>
                  <span className="font-mono text-[10px] text-foreground/90 w-6 text-right">
                    {val}
                  </span>
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Button Row: Explore, Compare, New Timeline, Copy, Bridge */}
      <div className="border-t border-border bg-card/80 p-3 sm:p-4 flex flex-wrap items-center gap-2 backdrop-blur-md">
        <button type="button" onClick={onExplore} className={`min-h-[44px] flex-1 min-w-[120px] rounded-full text-xs font-serif font-bold tracking-wider uppercase flex items-center justify-center gap-1.5 transition-all duration-200 cursor-pointer px-3 text-center ${exploring ? "bg-card border border-primary text-primary shadow-[0_0_12px_var(--glow-primary)]" : "ql-btn"}`} aria-label={exploring ? `Close detailed profile for ${name}` : `Explore detailed profile for ${name}`}>
          {exploring ? <>
              <ChevronUp className="size-3.5 shrink-0" />
              <span className="break-words">Close</span>
            </> : <>
              <Compass className="size-3.5 shrink-0" />
              <span className="break-words">Explore</span>
            </>}
        </button>

        <button type="button" onClick={onToggleCompare} disabled={compareDisabled && !comparing} aria-pressed={comparing} className={`min-h-[44px] min-w-0 px-3 sm:px-3.5 rounded-full text-xs font-mono transition-all duration-200 border flex items-center justify-center gap-1.5 cursor-pointer flex-1 min-[400px]:flex-initial text-center ${comparing ? "border-primary bg-primary/20 text-primary shadow-[0_0_12px_var(--glow-primary)]" : "border-border bg-muted/50 text-foreground hover:border-primary/50 hover:bg-accent"} ${compareDisabled && !comparing ? "opacity-40 cursor-not-allowed" : ""}`} aria-label={`Compare ${name} with another self`}>
          <Columns className="size-3.5 shrink-0" />
          <span className="break-words">{comparing ? "Comparing" : "Compare"}</span>
        </button>

        <button type="button" onClick={onRegenerate} className="min-h-[44px] min-w-[44px] px-3 rounded-full border border-border bg-muted/40 text-foreground hover:border-primary/50 hover:bg-accent hover:text-foreground text-xs flex items-center justify-center transition-all cursor-pointer min-w-0" aria-label={`Generate a new timeline variation for ${name}`} title="New timeline variation">
          <RefreshCw className="size-3.5 shrink-0" />
          <span className="sr-only sm:not-sr-only sm:ml-1.5 break-words">New timeline</span>
        </button>

        <button type="button" onClick={onCopy} className="min-h-[44px] min-w-[44px] px-3 rounded-full border border-border bg-muted/40 text-foreground hover:border-primary/50 hover:bg-accent hover:text-foreground text-xs flex items-center justify-center transition-all cursor-pointer min-w-0" aria-label={`Copy profile text for ${name}`} title="Copy profile text">
          <Copy className="size-3.5 shrink-0" />
          <span className="sr-only sm:not-sr-only sm:ml-1.5 break-words">Copy</span>
        </button>

        {onBridge && <button type="button" onClick={onBridge} className="ql-btn-ghost w-full min-h-[44px] rounded-full text-xs font-serif tracking-wider flex items-center justify-center gap-1.5 cursor-pointer mt-1 min-w-0 px-3 text-center" aria-label={`Bridge ${name} to reality in the Dream-to-Reality Bridge`}>
            <Sparkles className="size-3.5 text-primary shrink-0" />
            <span className="break-words">Bridge this self to reality</span>
          </button>}
      </div>
    </div>;
}